import type { JobDetailsView } from '@seek/job-details-view';
import type { SolMetadata } from '@seek/sol-js';
import { useQuery } from 'node_modules/@apollo/client';
import type { ComponentProps } from 'react';

import { getSerpLogoFromBrandId } from 'src/components/JobDetailsPage/getSerpLogoLinkFromBrandId';
import {
  DateTimeContext,
  DateTimeLength,
  JobDetailsRecommendedJobsDocument,
  Platform,
} from 'src/graphql/graphql';
import { getTimezone } from 'src/hooks/useTimezone';
import { useSelector } from 'src/store/react';
import { selectFeatureFlag, selectUserClientId } from 'src/store/selectors';
import { useMelwaysLocale, useMelwaysZone } from 'src/utils/melwaysHelper';

type CompetitiveJobType = NonNullable<
  ComponentProps<typeof JobDetailsView>['competitiveJobs']
>[number] & { solMetadata: SolMetadata }; // todo to be fixed if the JDV component consume solMetadata too

export const useCompetitiveJobs = (
  jobDetailsId: string,
  shouldSkip: boolean = false,
): CompetitiveJobType[] => {
  const skip =
    !useSelector(selectFeatureFlag('competitivePlacement')) ||
    shouldSkip ||
    !ENV.CLIENT;

  const { data, error, loading } = useQuery(JobDetailsRecommendedJobsDocument, {
    context: {
      headers: {
        'x-custom-features': 'application/features.seek.all+json',
        'seek-test': 'true', // todo clean up when feature flag COMPETITIVE_PLACEMENT is cleaned up
      },
    },
    variables: {
      jobDetailsId,
      zone: useMelwaysZone(),
      platform: Platform.Web,
      visitorId: useSelector(selectUserClientId),
      context: DateTimeContext.JobPosted,
      length: DateTimeLength.Short,
      timezone: getTimezone(),
      locale: useMelwaysLocale(),
    },
    skip,
    fetchPolicy: 'network-only',
    nextFetchPolicy: 'cache-first',
  });

  if (!skip && !loading && !error && data) {
    const competitiveJobs: CompetitiveJobType[] = [];

    data.jobDetails?.recommendedJobs?.forEach(({ job, solMetadata }) => {
      if (job) {
        const { id, title, advertiser, location, listedAt, products } = job;
        const { branding } = products ?? {};
        const cpJob: CompetitiveJobType = {
          solMetadata,
          company: {
            name: advertiser.name,
          },
          title,
          id,
          location: location.label,
          postedDateFormatted: listedAt?.label ?? '...',
          srcLogo: branding?.id
            ? getSerpLogoFromBrandId(branding.id)
            : undefined,
        };

        competitiveJobs.push(cpJob);
      }
    });

    return competitiveJobs;
  }
  return [];
};
